import { useState, useCallback, useEffect, useRef } from "react";
import { useDrumSynth, SOUNDS, SoundId, SoundSettings, getDefaultSettings } from "./useDrumSynth";
import { useSequencer } from "./useSequencer";
import { useMidi, GM_DRUM_MAP } from "../../hooks/useMidi";

const KEY_TO_SOUND: Record<string, SoundId> = {};
SOUNDS.forEach((s) => {
  KEY_TO_SOUND[s.key] = s.id;
});

export function RS808() {
  const [soundSettings, setSoundSettings] = useState<Record<SoundId, SoundSettings>>(getDefaultSettings);
  const [selectedSound, setSelectedSound] = useState<SoundId>("kick");
  const [distortion, setDistortionState] = useState(false);
  const [showIcons, setShowIcons] = useState(false);
  const [activePad, setActivePad] = useState<SoundId | null>(null);
  const [displayText, setDisplayText] = useState("READY");
  const [velocitySensitivity, setVelocitySensitivity] = useState(0.7);

  const drumSynth = useDrumSynth();

  const triggerSound = useCallback(
    (sound: SoundId, velocity = 127) => {
      drumSynth.playSound(sound, soundSettings[sound], velocity);
      setDisplayText(sound.toUpperCase());
      setTimeout(() => setDisplayText("READY"), 150);
    },
    [drumSynth, soundSettings]
  );

  const sequencer = useSequencer(triggerSound);

  const handlePadTrigger = useCallback(
    (sound: SoundId, velocity = 127) => {
      setSelectedSound(sound);
      setActivePad(sound);
      triggerSound(sound, velocity);
      setTimeout(() => setActivePad(null), 80);
    },
    [triggerSound]
  );

  // MIDI support - map MIDI drum notes to pads
  const midi = useMidi({
    onNoteOn: (midiNote, velocity) => {
      const soundId = GM_DRUM_MAP[midiNote] as SoundId | undefined;
      if (soundId && SOUNDS.some((s) => s.id === soundId)) {
        const effectiveVelocity = Math.round(127 - (127 - velocity) * velocitySensitivity);
        handlePadTrigger(soundId, effectiveVelocity);
      }
    },
  });

  const handleDistortionToggle = useCallback(() => {
    setDistortionState((prev) => {
      const next = !prev;
      drumSynth.setDistortion(next);
      return next;
    });
  }, [drumSynth]);

  const updateSoundParam = useCallback(
    (param: keyof SoundSettings, delta: number) => {
      setSoundSettings((prev) => {
        const current = prev[selectedSound];
        let newValue: number;

        if (param === "volume") {
          newValue = Math.max(0, Math.min(1.1, current.volume + delta * 0.11));
        } else if (param === "pitch") {
          newValue = Math.max(-12, Math.min(12, current.pitch + delta * 2));
        } else {
          newValue = Math.max(0, Math.min(1, current.decay + delta * 0.1));
        }

        return {
          ...prev,
          [selectedSound]: { ...current, [param]: newValue },
        };
      });
    },
    [selectedSound]
  );

  // Keyboard handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();

      if (key === " ") {
        e.preventDefault();
        sequencer.play();
        return;
      }

      if (KEY_TO_SOUND[key]) {
        e.preventDefault();
        handlePadTrigger(KEY_TO_SOUND[key]);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handlePadTrigger, sequencer]);

  // BPM knob drag handling
  const bpmKnobRef = useRef<HTMLDivElement>(null);
  const bpmDragState = useRef({ active: false, startY: 0, startBpm: 120 });

  useEffect(() => {
    const handleMove = (clientY: number) => {
      if (!bpmDragState.current.active) return;
      const delta = (bpmDragState.current.startY - clientY) / 2;
      const newBpm = Math.round(bpmDragState.current.startBpm + delta);
      sequencer.updateBpm(newBpm);
    };

    const handleEnd = () => {
      bpmDragState.current.active = false;
    };

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      if (bpmDragState.current.active) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [sequencer]);

  const handleBpmKnobStart = (clientY: number) => {
    bpmDragState.current = {
      active: true,
      startY: clientY,
      startBpm: sequencer.bpm,
    };
  };

  const currentSettings = soundSettings[selectedSound];
  const volumePercent = Math.round((currentSettings.volume / 1.1) * 100);
  const decayPercent = Math.round(currentSettings.decay * 100);

  return (
    <div className="bg-[#f5f0e6] rounded-2xl p-5 max-w-2xl mx-auto font-mono select-none shadow-[0_2px_0_#d9d4c8,0_4px_0_#ccc7bb,0_8px_24px_rgba(0,0,0,0.2)]">
        {/* Header */}
        <div className="flex justify-between items-center mb-5 pb-3 border-b-2 border-[#e0dbd0]">
          <div className="text-lg font-extrabold tracking-tight text-[#1a1a1a]">RS-808</div>
          <div className="flex items-center gap-2">
            <div
              className={`text-[10px] tracking-wider px-2 py-1 rounded ${
                midi.connected
                  ? "bg-[#ff6b35] text-white font-semibold"
                  : "bg-[#e0dbd0] text-[#aaa]"
              }`}
              title={midi.connected ? (midi.deviceName || "MIDI Connected") : "MIDI not connected"}
            >
              MIDI
            </div>
            <div className="text-[10px] text-[#999] tracking-wider bg-[#e0dbd0] px-2 py-1 rounded">
              RHYTHM COMPOSER
            </div>
          </div>
        </div>

      {/* Display */}
      <div className="bg-[#1a2a1a] rounded-md px-4 py-3 mb-5 flex items-center justify-between border-2 border-[#0f1a0f]">
        <div className="text-xl text-[#00ff88] tracking-widest font-semibold drop-shadow-[0_0_10px_#00ff88]">
          {displayText}
        </div>
        <div className="text-[10px] text-[#00aa55] text-right">
          <div>PITCH: {currentSettings.pitch > 0 ? "+" : ""}{currentSettings.pitch}</div>
        </div>
      </div>

      {/* Pads */}
      <div className="grid grid-cols-4 gap-2.5 mb-5">
        {SOUNDS.map((sound) => (
          <button
            key={sound.id}
            onPointerDown={(e) => {
              e.preventDefault();
              handlePadTrigger(sound.id);
            }}
            className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-1 transition-all cursor-pointer min-h-[60px] touch-none ${
              activePad === sound.id
                ? "translate-y-[3px] shadow-[0_0_0_#b8b3a8,0_1px_4px_rgba(0,0,0,0.1)] bg-gradient-to-br from-[#ff6b35] to-[#ff8142]"
                : "shadow-[0_3px_0_#b8b3a8,0_4px_8px_rgba(0,0,0,0.15)] bg-gradient-to-br from-[#e8e3d8] to-[#d8d3c8]"
            }`}
          >
            <span
              className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                activePad === sound.id
                  ? "text-white bg-black/20"
                  : "text-[#666] bg-black/[0.08]"
              }`}
            >
              {sound.key.toUpperCase()}
            </span>
            <span
              className={`text-[8px] uppercase tracking-wide ${
                activePad === sound.id ? "text-white/90" : "text-[#888]"
              }`}
            >
              {sound.name}
            </span>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex justify-around items-start pt-4 border-t-2 border-[#e0dbd0]">
        {/* Volume */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="w-11 h-11 rounded-full cursor-pointer relative shadow-[0_3px_6px_rgba(0,0,0,0.4)]"
            style={{
              background: "linear-gradient(145deg, #3a3a3a, #222)",
            }}
          >
            <div
              className="absolute inset-1 rounded-full"
              style={{ background: "linear-gradient(145deg, #2a2a2a, #1a1a1a)" }}
            />
            <div
              className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[3px] h-2.5 rounded bg-[#ff6b35]"
              style={{
                transform: `translateX(-50%) rotate(${((currentSettings.volume / 1.1) - 0.5) * 270}deg)`,
                transformOrigin: "center 16px",
              }}
            />
          </div>
          <span className="text-[9px] text-[#666] uppercase tracking-wider font-semibold">Volume</span>
          <span className="text-[11px] text-[#333] font-semibold">{volumePercent}%</span>
          <div className="flex gap-1 mt-1 md:hidden">
            <button
              onClick={() => updateSoundParam("volume", -1)}
              className="w-6 h-5 rounded bg-gradient-to-b from-[#3a3a3a] to-[#222] text-white text-xs"
            >
              −
            </button>
            <button
              onClick={() => updateSoundParam("volume", 1)}
              className="w-6 h-5 rounded bg-gradient-to-b from-[#3a3a3a] to-[#222] text-white text-xs"
            >
              +
            </button>
          </div>
        </div>

        {/* Pitch */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="w-11 h-11 rounded-full cursor-pointer relative shadow-[0_3px_6px_rgba(0,0,0,0.4)]"
            style={{
              background: "linear-gradient(145deg, #3a3a3a, #222)",
            }}
          >
            <div
              className="absolute inset-1 rounded-full"
              style={{ background: "linear-gradient(145deg, #2a2a2a, #1a1a1a)" }}
            />
            <div
              className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[3px] h-2.5 rounded bg-[#ff6b35]"
              style={{
                transform: `translateX(-50%) rotate(${(currentSettings.pitch / 24) * 270}deg)`,
                transformOrigin: "center 16px",
              }}
            />
          </div>
          <span className="text-[9px] text-[#666] uppercase tracking-wider font-semibold">Pitch</span>
          <span className="text-[11px] text-[#333] font-semibold">
            {currentSettings.pitch > 0 ? "+" : ""}{currentSettings.pitch}
          </span>
          <div className="flex gap-1 mt-1 md:hidden">
            <button
              onClick={() => updateSoundParam("pitch", -1)}
              className="w-6 h-5 rounded bg-gradient-to-b from-[#3a3a3a] to-[#222] text-white text-xs"
            >
              −
            </button>
            <button
              onClick={() => updateSoundParam("pitch", 1)}
              className="w-6 h-5 rounded bg-gradient-to-b from-[#3a3a3a] to-[#222] text-white text-xs"
            >
              +
            </button>
          </div>
        </div>

        {/* Decay */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="w-11 h-11 rounded-full cursor-pointer relative shadow-[0_3px_6px_rgba(0,0,0,0.4)]"
            style={{
              background: "linear-gradient(145deg, #3a3a3a, #222)",
            }}
          >
            <div
              className="absolute inset-1 rounded-full"
              style={{ background: "linear-gradient(145deg, #2a2a2a, #1a1a1a)" }}
            />
            <div
              className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[3px] h-2.5 rounded bg-[#ff6b35]"
              style={{
                transform: `translateX(-50%) rotate(${(currentSettings.decay - 0.5) * 270}deg)`,
                transformOrigin: "center 16px",
              }}
            />
          </div>
          <span className="text-[9px] text-[#666] uppercase tracking-wider font-semibold">Decay</span>
          <span className="text-[11px] text-[#333] font-semibold">{decayPercent}%</span>
          <div className="flex gap-1 mt-1 md:hidden">
            <button
              onClick={() => updateSoundParam("decay", -1)}
              className="w-6 h-5 rounded bg-gradient-to-b from-[#3a3a3a] to-[#222] text-white text-xs"
            >
              −
            </button>
            <button
              onClick={() => updateSoundParam("decay", 1)}
              className="w-6 h-5 rounded bg-gradient-to-b from-[#3a3a3a] to-[#222] text-white text-xs"
            >
              +
            </button>
          </div>
        </div>

        {/* Distortion */}
        <div className="flex flex-col items-center gap-2">
          <div
            onClick={handleDistortionToggle}
            className="w-12 h-6 rounded-xl cursor-pointer relative shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]"
            style={{ background: "#2a2a2a" }}
          >
            <div
              className={`absolute top-[3px] w-[18px] h-[18px] rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.3)] transition-transform ${
                distortion ? "left-[27px]" : "left-[3px]"
              }`}
              style={{
                background: distortion
                  ? "linear-gradient(145deg, #ff6b35, #ff8142)"
                  : "linear-gradient(145deg, #e0dbd0, #ccc7bb)",
              }}
            />
          </div>
          <span className="text-[9px] text-[#666] uppercase tracking-wider font-semibold">Dirt</span>
        </div>
      </div>

      {/* Sequencer */}
      <div className="mt-4 pt-4 border-t-2 border-[#e0dbd0]">
        <div className="flex justify-between items-center mb-2.5">
          <div>
            <span className="text-[9px] text-[#666] uppercase tracking-wider font-semibold">Seq: </span>
            <span className="text-[10px] text-[#ff6b35] font-bold uppercase">
              {selectedSound.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* BPM Knob */}
            <div
              ref={bpmKnobRef}
              onMouseDown={(e) => handleBpmKnobStart(e.clientY)}
              onTouchStart={(e) => {
                e.preventDefault();
                handleBpmKnobStart(e.touches[0].clientY);
              }}
              className="w-7 h-7 rounded-full cursor-ns-resize relative shadow-[0_2px_4px_rgba(0,0,0,0.3)] touch-none"
              style={{ background: "linear-gradient(145deg, #3a3a3a, #222)" }}
              title="Drag to change BPM"
            >
              <div
                className="absolute inset-[3px] rounded-full"
                style={{ background: "linear-gradient(145deg, #2a2a2a, #1a1a1a)" }}
              />
              <div
                className="absolute top-1 left-1/2 -translate-x-1/2 w-0.5 h-1.5 rounded bg-[#00ff88]"
                style={{
                  transform: `translateX(-50%) rotate(${((sequencer.bpm - 130) / 70) * 135}deg)`,
                  transformOrigin: "center 10px",
                }}
              />
            </div>
            <span className="text-[10px] text-[#666] font-semibold min-w-[28px] text-right">
              {sequencer.bpm}
            </span>
            {/* Play button */}
            <button
              onClick={() => sequencer.play()}
              className="w-8 h-6 rounded flex items-center justify-center shadow-[0_2px_4px_rgba(0,0,0,0.3)] active:translate-y-px"
              style={{ background: "linear-gradient(145deg, #3a3a3a, #222)" }}
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill={sequencer.playing ? "#ff6b35" : "#ccc"}>
                {sequencer.playing ? (
                  <rect x="6" y="4" width="4" height="16" />
                ) : (
                  <polygon points="5,3 19,12 5,21" />
                )}
                {sequencer.playing && <rect x="14" y="4" width="4" height="16" />}
              </svg>
            </button>
          </div>
        </div>

        {/* Steps - 2 rows on mobile, 1 row on desktop */}
        <div className="grid grid-cols-8 md:grid-cols-16 gap-1">
          {Array.from({ length: 16 }).map((_, i) => {
            const isActive = sequencer.pattern[selectedSound][i];
            const isCurrent = sequencer.currentStep === i && sequencer.playing;
            const hasOther = SOUNDS.some(
              (s) => s.id !== selectedSound && sequencer.pattern[s.id][i]
            );

            return (
              <button
                key={i}
                onClick={() => sequencer.toggleStep(selectedSound, i)}
                className={`h-8 md:h-5 rounded-sm relative transition-all ${
                  isActive
                    ? "bg-[#ff6b35] shadow-[0_0_8px_rgba(255,107,53,0.5)]"
                    : "bg-[#d8d3c8]"
                }`}
              >
                {hasOther && (
                  <span
                    className={`absolute top-1 md:top-[3px] left-1/2 -translate-x-1/2 w-1.5 md:w-1 h-1.5 md:h-1 rounded-full ${
                      isActive ? "bg-white/60" : "bg-[#999]"
                    }`}
                  />
                )}
                {isCurrent && (
                  <span className="absolute -bottom-1.5 md:-bottom-1 left-1/2 -translate-x-1/2 w-1.5 md:w-1 h-1.5 md:h-1 rounded-full bg-[#00ff88]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex justify-between items-center text-[9px] text-[#aaa] tracking-wide">
        <span className="hidden md:inline">Keys: 1-4 · Q-R · A-F · Z-V · Space: Play</span>
        <span className="md:hidden">Tap pads to play</span>
        <div className="flex items-center gap-3">
          {midi.connected && (
            <div className="flex items-center gap-1.5">
              <span className="text-[#ff6b35] font-semibold">VEL</span>
              <input
                type="range"
                min="0"
                max="100"
                value={velocitySensitivity * 100}
                onChange={(e) => setVelocitySensitivity(Number(e.target.value) / 100)}
                className="w-12 h-1 accent-[#ff6b35]"
              />
            </div>
          )}
          <button
            onClick={() => sequencer.clearPattern()}
            className="text-[#999] font-semibold px-1.5 py-0.5 rounded hover:text-[#ff6b35] hover:bg-[rgba(255,107,53,0.1)] transition-colors"
          >
            CLR
          </button>
        </div>
      </div>

    </div>
  );
}
